import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { pageTitle } from '../../../util/pageTitles';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import _ from 'lodash';

import { analysisIsLoading, setPageName } from '../../App/AppActions';
import { setChart, updateSelectedOptions, saveConfig } from '../../../components/Filter/FilterActions';
import Filter from '../../../components/Filter/Filter';
import { getData, runAnalysis, isLoading } from '../ProcessImprovementActions';
import { getSelectionValues } from '../../../util/filterUtils';
import { trackTeamDashboardView } from '../../../util/mixpanel';

import InsightCard from '../components/InsightCard';
import { PROCESS_IMPROVEMENT_WIDGET_IDS, PROCESS_IMPROVEMENT_WIDGETS_SMALL, PROCESS_IMPROVEMENT_WIDGETS } from '../ProcessImprovementConstants';
import { GridStackManager } from '../../../util/gridStackUtils';

// Import Styles
import '../ProcessImprovement.scss';

const THRESHOLDS_INFO_HIDE_DELAY = 400;

export class ProcessImprovement extends Component {
  constructor(props) {
    super(props);
    this.gridRef = React.createRef();

    // Initialize widget configuration based on saved order or default
    const defaultConfig = window.innerWidth > 1500 ? PROCESS_IMPROVEMENT_WIDGETS : PROCESS_IMPROVEMENT_WIDGETS_SMALL;

    this.gridManager = new GridStackManager(this.gridRef, {
      widgetConfig: defaultConfig,
      cellHeight: 470,
      minWidgetWidth: 316
    });

    // Restore configuration from saved widget layout if available
    if (props.selectedWidgets && props.selectedWidgets.length > 0) {
      const savedData = {
        widgets: props.selectedWidgets,
        layoutData: props.widgetLayout
      };
      this.gridManager.restoreFromSavedOrder(savedData, defaultConfig);
    }

    this.gridManager.setDefaultWidgetOrder(PROCESS_IMPROVEMENT_WIDGET_IDS);
    this.grid = null;
    this.overThresholdsInfo = false;

    this.state = {
      oct: 0,
      ct: 0,
      wip: 0,
      wipAvgAge: 0,
      showFlowMetrics: false,
      showProcessImprovement: false,
      customizeMode: false,
      tempSelectedWidgets: null,
      thresholdsInfoOpen: false,
    };

    props.setPageName('Process Improvement');
  }

  componentDidMount() {
    if (this.props.dashboard && this.props.dashboard._id) {
      this.props.setChart('process');
      this.getData();
      trackTeamDashboardView('process');
    }
    // a font weight first used by a card's content loads after the content shows and rewraps its text
    this.fonts = document.fonts;
    this.fonts?.addEventListener('loadingdone', this.fitCardHeight);
    this.loadGridStack().then(() => {
      this.forceLayoutUpdate(() => {
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, 100);
      });
      if (window.ResizeObserver && this.gridRef.current) {
        this.cardSizeObserver = new window.ResizeObserver(this.scheduleCardHeightFit);
        this.cardSizeObserver.observe(this.gridRef.current);
      }
      this.scheduleCardHeightFit();
    });
  }

  componentWillUnmount() {
    if (this.cardSizeObserver) this.cardSizeObserver.disconnect();
    this.fonts?.removeEventListener('loadingdone', this.fitCardHeight);
    clearTimeout(this.cardHeightTimer);
    clearTimeout(this.thresholdsInfoTimer);
    this.gridManager.destroy();
  }

  scheduleCardHeightFit = () => {
    clearTimeout(this.cardHeightTimer);
    this.cardHeightTimer = setTimeout(this.fitCardHeight, 150);
  };

  fitCardHeight = () => {
    if (!this.grid || !this.gridRef.current) return;
    const needs = Array.from(this.gridRef.current.querySelectorAll('.card-item')).map(card => {
      const header = card.querySelector('.card-header');
      const content = card.querySelector('.card-content');
      const info = card.querySelector('.card-info');
      if (!header || !content || !info) return 0;
      return header.offsetHeight + (info.getBoundingClientRect().bottom - content.getBoundingClientRect().top) + 18;
    });
    const need = Math.ceil(Math.max(0, ...needs));
    if (!need) return;
    const item = this.gridRef.current.querySelector('.grid-stack-item');
    const card = item && item.querySelector('.card-item');
    const margin = item && card ? Math.max(0, item.offsetHeight - card.offsetHeight) : 0;
    const height = Math.min(540, Math.max(380, need + margin));
    if (height === this.cardHeight) return;
    this.cardHeight = height;
    this.gridManager.isUpdatingHeight = true;
    this.gridManager.withoutAnimation(() => this.grid.cellHeight(height));
    setTimeout(() => {
      this.gridManager.isUpdatingHeight = false;
    }, 100);
  };

  forceLayoutUpdate = (callback) => {
    if (this.grid) {
      const availableWidgets = this.getAvailableWidgets();

      // During customization, use temp widgets; otherwise use props
      let selectedWidgets;
      if (this.state.customizeMode && this.state.tempSelectedWidgets) {
        selectedWidgets = this.state.tempSelectedWidgets.filter(widgetId => availableWidgets.includes(widgetId));
      } else {
        selectedWidgets = this.props.selectedWidgets && this.props.selectedWidgets.length > 0
          ? this.props.selectedWidgets.filter(widgetId => availableWidgets.includes(widgetId))
          : availableWidgets;
      }

      this.gridManager.handleGoBackFromCustomize(
        [...selectedWidgets],
        () => {
          this.forceUpdate();
          if (typeof callback === 'function') {
            callback();
          }
        }
      );
    }
  };

  componentDidUpdate(prevProps) {
    this.isLoaded();
    this.fitCardHeight();
    if (
      prevProps.analysisLoading !== this.props.analysisLoading &&
      !this.props.analysisLoading
    ) {
      this.props.setChart('process');
      this.setState({ pendingUpdate: true });
    }

    // Detect when showFlowEfficiency changes (queue list selection changed)
    const prevAllQueueLists = prevProps.allQueueLists || [];
    const currentAllQueueLists = this.props.allQueueLists || [];
    const prevShowFlowEfficiency = prevAllQueueLists.length > 0 &&
      getSelectionValues(prevProps.queueLists, prevAllQueueLists).length < prevAllQueueLists.length;
    const currentShowFlowEfficiency = currentAllQueueLists.length > 0 &&
      getSelectionValues(this.props.queueLists, currentAllQueueLists).length < currentAllQueueLists.length;

    if (prevShowFlowEfficiency !== currentShowFlowEfficiency) {
      // Flow Efficiency visibility changed - recalculate grid layout
      this.forceLayoutUpdate();
    }

    if (
      this.props.analysisDates &&
      this.props.analysisData && this.props.cards &&
      (prevProps.analysisDates != this.props.analysisDates ||
        prevProps.dataHash != this.props.dataHash ||
        prevProps.hash != this.props.hash ||
        prevProps.excludeWeekends !== this.props.excludeWeekends ||
        prevProps.workStart !== this.props.workStart ||
        prevProps.workEnd !== this.props.workEnd ||
        !_.isEqual(prevProps.holidays, this.props.holidays) ||
        prevProps.enabledColors !== this.props.enabledColors) &&
      this.props.analysisLoading === false
    ) {
      this.props.runAnalysis();
    }

    if (
      (prevProps.dataHash != this.props.dataHash ||
        prevProps.hash != this.props.hash ||
        prevProps.excludeWeekends !== this.props.excludeWeekends ||
        prevProps.workStart !== this.props.workStart ||
        prevProps.workEnd !== this.props.workEnd ||
        !_.isEqual(prevProps.holidays, this.props.holidays) ||
        prevProps.enabledColors !== this.props.enabledColors) &&
      this.props.analysisLoading
    ) {
      this.setState({ pendingUpdate: true });
    }

    if (
      prevProps.analysisLoading !== this.props.analysisLoading &&
      !this.props.analysisLoading && !this.props.analysisRunning &&
      this.state.pendingUpdate
    ) {
      this.setState({ pendingUpdate: false });
      this.props.runAnalysis();
    }

    const widgetsChanged = !_.isEqual(prevProps.selectedWidgets, this.props.selectedWidgets);
    const layoutChanged = !_.isEqual(prevProps.widgetLayout, this.props.widgetLayout);

    if (widgetsChanged || layoutChanged) {
      const availableWidgets = this.getAvailableWidgets();
      const newSelectedWidgets = this.props.selectedWidgets ?
        this.props.selectedWidgets.filter(widgetId => availableWidgets.includes(widgetId)) :
        availableWidgets;

      // Restore configuration from saved order if available
      if (this.props.selectedWidgets && this.props.selectedWidgets.length > 0 &&
          this.props.widgetLayout && this.props.widgetLayout.length > 0) {
        const defaultConfig = window.innerWidth > 1500 ? PROCESS_IMPROVEMENT_WIDGETS : PROCESS_IMPROVEMENT_WIDGETS_SMALL;
        const savedData = {
          widgets: this.props.selectedWidgets,
          layoutData: this.props.widgetLayout
        };
        this.gridManager.restoreFromSavedOrder(savedData, defaultConfig);
      }

      this.gridManager.handleGoBackFromCustomize(newSelectedWidgets, () => this.forceUpdate());
    }
  }

  async loadGridStack() {
    this.grid = await this.gridManager.loadGridStack();
    this.applyInitialLayout();
  }

  applyInitialLayout = () => {
    if (this.grid) {
      this.forceLayoutUpdate();
    }
  };

  isLoaded() {
    if (
      this.props.analysisLoading &&
      !this.props.analysisRunning &&
      !this.props.ctAnalysisLoading &&
      !this.props.ctbAnalysisLoading &&
      !this.props.wipAnalysisLoading &&
      !this.props.wipAgeAnalysisLoading &&
      !this.props.thAnalysisLoading &&
      !this.props.feAnalysisLoading
    ) {
      this.props.analysisIsRunning(false);
    }
  }

  getData() {
    if (!this.props.analysisLoading && !this.props.cards) {
      this.props.analysisIsLoading(true);
      this.props.getData();
    }
  }

  getAvailableWidgets = () => {
    const { queueLists, allQueueLists } = this.props;
    const showFlowEfficiency = getSelectionValues(queueLists, allQueueLists).length < allQueueLists.length;
    
    if (!showFlowEfficiency) {
      return PROCESS_IMPROVEMENT_WIDGET_IDS.filter(widgetId => widgetId !== 'fe');
    }
    return PROCESS_IMPROVEMENT_WIDGET_IDS;
  };

  isWidgetVisible = (widgetId) => {
    const { queueLists, allQueueLists } = this.props;
    const showFlowEfficiency = getSelectionValues(queueLists, allQueueLists).length < allQueueLists.length;
    
    // Hide flow efficiency widget if not available
    if (widgetId === 'fe' && !showFlowEfficiency) {
      return false;
    }
    
    const selectedWidgets = this.state.customizeMode 
      ? this.state.tempSelectedWidgets 
      : this.props.selectedWidgets;
    
    if (!selectedWidgets || selectedWidgets.length === 0) {
      return true;
    }
    
    if (Array.isArray(selectedWidgets)) {
      return selectedWidgets.includes(widgetId);
    }
    
    return true;
  };

  handleWidgetSelectionChange = (selectedWidgets) => {
    this.forceUpdate();
  };

  handleCustomizeViewClick = () => {
    const availableWidgets = this.getAvailableWidgets();
    const currentSelected = this.props.selectedWidgets || availableWidgets;
    const filteredSelected = currentSelected.filter(widgetId => availableWidgets.includes(widgetId));
    
    this.setState({
      customizeMode: true,
      tempSelectedWidgets: [...filteredSelected],
    }, () => {
      this.gridManager.handleCustomizeViewClick(this.state.tempSelectedWidgets);
    });
  };

  handleGoBackFromCustomize = () => {
    const availableWidgets = this.getAvailableWidgets();
    const currentSelected = this.props.selectedWidgets && this.props.selectedWidgets.length > 0 
      ? this.props.selectedWidgets 
      : availableWidgets;
    const filteredSelected = currentSelected.filter(widgetId => availableWidgets.includes(widgetId));
    
    this.setState({
      customizeMode: false,
      tempSelectedWidgets: null,
    }, () => {
      this.gridManager.handleGoBackFromCustomize(
         [...filteredSelected],
        () => this.forceUpdate()
      );
    });
  };

  handleApplyCustomization = () => {
    const layoutResult = this.gridManager.handleApplyCustomization();

    if (layoutResult.widgets && layoutResult.widgets.length > 0) {
      // Save both widget order and complete layout data
      this.props.updateSelectedOptions('widgets', layoutResult.widgets);
      // Also save layout data
      if (layoutResult.layoutData && layoutResult.layoutData.length > 0) {
        this.props.updateSelectedOptions('widgetLayout', layoutResult.layoutData);
      }
      this.props.saveConfig();
    }

    this.setState({
      customizeMode: false,
      tempSelectedWidgets: null,
    });
  };

  handleTempWidgetSelectionChange = (selectedWidgets) => {
    this.setState({ tempSelectedWidgets: selectedWidgets }, () => {
      this.gridManager.handleTempWidgetSelectionChange(selectedWidgets);
    });
  };

  toggleThresholdsInfo = (open) => {
    if (open) clearTimeout(this.thresholdsInfoTimer);
    if (open || !this.overThresholdsInfo) {
      this.setState({ thresholdsInfoOpen: open });
    }
  };

  enterThresholdsInfo = () => {
    this.overThresholdsInfo = true;
  };

  leaveThresholdsInfo = () => {
    this.overThresholdsInfo = false;
    clearTimeout(this.thresholdsInfoTimer);
    this.thresholdsInfoTimer = setTimeout(() => this.toggleThresholdsInfo(false), THRESHOLDS_INFO_HIDE_DELAY);
  };

  render() {
    const { queueLists, allQueueLists } = this.props;
    const showFlowEfficiency = getSelectionValues(queueLists, allQueueLists).length < allQueueLists.length;
    const totalItems = this.props.cards ? this.props.cards.length : 0;

    const tresholdsCalculationsTooltip = (
      <Tooltip id="tresholdsTooltip" className="tooltip_white calculate_info" onMouseEnter={this.enterThresholdsInfo} onMouseLeave={this.leaveThresholdsInfo}>
        <h3>How thresholds work</h3>
        <p>Your thresholds show the expected range for your team, based on its recent performance.</p>
        <p>
          Your thresholds are set by analyzing a time-ordered sequence of data, using between 4 to 20 data points (with 20
          as the default).
        </p>
        <p>
          To flag a low or high status for your flow metrics, we look for a signal—a significant deviation from your
          typical process behavior.
        </p>
        <p>
          When a signal is detected, the benchmarks are frozen, and no new data points are included. Once at least 4 new
          data points are available, they are used to establish a new baseline.
        </p>
        <p>We use last three months data in this analysis.</p>
        <dl>
          <dt>Stable</dt>
          <dd>Within the expected range.</dd>
          <dt>High / low</dt>
          <dd>Outside the expected range.</dd>
          <dt>Unstable</dt>
          <dd>A meaningful change has been detected.</dd>
          <dt>At risk</dt>
          <dd>WIP or WIP Age is approaching its upper threshold.</dd>
        </dl>
        <a
          href="https://getnave.com/blog/thresholds-that-drive-continuous-improvement/"
          target="_blank"
          rel="noopener noreferrer"
        >
          See exactly how thresholds are calculated →
        </a>
      </Tooltip>
    );

    let widgetsToRender = this.props.selectedWidgets;
    const availableWidgets = this.getAvailableWidgets();
    
    if (!this.state.customizeMode && (!widgetsToRender || widgetsToRender.length === 0)) {
      widgetsToRender = availableWidgets;
    } else if (widgetsToRender) {
      // Filter out unavailable widgets from selected widgets
      widgetsToRender = widgetsToRender.filter(widgetId => availableWidgets.includes(widgetId));
    }

    return (
      <div className="background_gray overview_page">
        <Helmet>
          <title>{pageTitle(`Process Improvement${this.props.dashboard ? ' | ' + this.props.dashboard.name : ''}`)}</title>
        </Helmet>
        {this.props.cards && !this.props.analysisLoading && (
          <Filter 
            isOverview 
            isProcessImprovement 
            colors 
            disabledLists={['todo']} 
            onWidgetSelectionChange={this.handleWidgetSelectionChange}
            customizeMode={this.state.customizeMode}
            onCustomizeViewClick={this.handleCustomizeViewClick}
            onGoBackFromCustomize={this.handleGoBackFromCustomize}
            onApplyCustomization={this.handleApplyCustomization}
            onTempWidgetSelectionChange={this.handleTempWidgetSelectionChange}
            tempSelectedWidgets={this.state.tempSelectedWidgets}
            selectedWidgets={widgetsToRender}
            availableWidgets={availableWidgets}
          />
        )}
        <div className="process">
          <div className="process-header">
            <h1 className="process-title">Process Improvement</h1>
            <div className="process-header-right-elements">
              <OverlayTrigger
                overlay={tresholdsCalculationsTooltip}
                show={this.state.thresholdsInfoOpen}
                onToggle={this.toggleThresholdsInfo}
                delay={{ show: 250, hide: THRESHOLDS_INFO_HIDE_DELAY }}
                placement="bottom-end"
                trigger={['hover', 'focus', 'click']}
              >
                <span className="calculate_tresholds">
                  <span className="more_info" />
                  How we calculate your thresholds
                </span>
              </OverlayTrigger>
            </div>
          </div>

          <div className="grid-stack" ref={this.gridRef}>
            <InsightCard
              app={this.props.app}
              ctAnalysis={this.props.ctAnalysis}
              ctAnalysisLoading={this.props.ctAnalysisLoading}
              wipAgeAnalysis={this.props.wipAgeAnalysis}
              wipAgeAnalysisLoading={this.props.wipAgeAnalysisLoading}
              wipAnalysis={this.props.wipAnalysis}
              wipAnalysisLoading={this.props.wipAnalysisLoading}
              ctbAnalysis={this.props.ctbAnalysis}
              ctbAnalysisisLoading={this.props.ctbAnalysisisLoading}
              thAnalysis={this.props.thAnalysis}
              thAnalysisisLoading={this.props.thAnalysis}
              feAnalysis={this.props.feAnalysis}
              feAnalysisisLoading={this.props.feAnalysisisLoading}
              hasFlowEfficiency={showFlowEfficiency}
              dashboardName={this.props.dashboard ? this.props.dashboard.name : ''}
              dashboardId={this.props.dashboard ? this.props.dashboard._id : ''}
              userSubscription={this.props.user ? this.props.user.status : ''}
              isWidgetVisible={this.isWidgetVisible}
              customizeMode={this.state.customizeMode}
              availableWidgets={availableWidgets}
              excludeWeekendOpts={{
                type: this.props.excludeWeekends,
                workStart: this.props.workStart,
                workEnd: this.props.workEnd,
                holidays: this.props.holidays
              }}
              useWorkingHoursInCycleTimeDays={this.props.useWorkingHoursInCycleTimeDays}
            />
          </div>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    setChart: chart => dispatch(setChart(chart)),
    getData: () => dispatch(getData()),
    analysisIsLoading: loading => dispatch(analysisIsLoading(loading)),
    analysisIsRunning: loading => dispatch(isLoading(loading)),
    setPageName: name => dispatch(setPageName(name)),
    runAnalysis: () => dispatch(runAnalysis()),
    updateSelectedOptions: (field, value) => dispatch(updateSelectedOptions(field, value, 'process')),
    saveConfig: () => dispatch(saveConfig()),
  };
};

const mapStateToProps = state => {
  return {
    app: state.app.app,
    user: state.auth.user,
    filtersDataHash: state.app.filtersDataHash,
    enabledColors: state.filters.enabledColors,
    timePrecision: state.filters.timePrecision,
    excludeWeekends: state.filters.excludeWeekends,
    workStart: state.filters.workStart,
    workEnd: state.filters.workEnd,
    holidays: state.filters.holidays,
    queueLists: state.filters.process.selectedQueueLists,
    allQueueLists: state.filters.allQueueLists,
    hash: state.filters.process.hash,
    analysisRunning: state.process.analysisRunning,
    analysisLoading: state.app.analysisLoading,   
    dataHash: state.app.analysisDataHash,
    analysisData: state.app.analysisData,
    dashboard: state.dashboards.selected,
    cards: state.app.analysisData.cards,
    analysisDates: state.filters.analysisDates,
    ctAnalysis: state.process.ctAnalysis,
    ctAnalysisLoading: state.process.ctAnalysisLoading,
    wipAgeAnalysis: state.process.wipAgeAnalysis,
    wipAgeAnalysisLoading: state.process.wipAgeAnalysisLoading,
    wipAnalysis: state.process.wipAnalysis,
    wipAnalysisLoading: state.process.wipAnalysisLoading,
    ctbAnalysis: state.process.ctbAnalysis,
    ctbAnalysisLoading: state.process.ctbAnalysisLoading,
    thAnalysis: state.process.thAnalysis,
    thAnalysisLoading: state.process.thAnalysisLoading,
    feAnalysis: state.process.feAnalysis,
    feAnalysisLoading: state.process.feAnalysisLoading,
    dataIsLoading: state.app.isLoading,
    selectedWidgets: state.filters.process.selectedWidgets,
    widgetLayout: state.filters.process.selectedWidgetLayout,
    useWorkingHoursInCycleTimeDays: state.auth.user.config && state.auth.user.config.useWorkingHoursInCycleTimeDays
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProcessImprovement);
